import { Fragment } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
} from "@mui/material";
import EmptyState from "./EmptyState";
import LoadingState from "./LoadingState";

/**
 * DataTable — Phase 2 reusable component.
 *
 * A generic table shell: renders a header from a `columns` definition, and
 * either the built-in loading/empty states or the caller's own row markup.
 *
 * Deliberately NOT included: sorting, filtering, pagination logic, or row
 * selection state — those all vary per existing page (e.g. JobRequirements'
 * expandable rows, Candidates' bulk-select checkboxes) and must be preserved
 * exactly as each page currently implements them. This component only
 * standardizes the table shell/empty/loading presentation; every page keeps
 * its own existing row logic when it adopts this component.
 *
 * Props:
 *   columns        - [{ key, label, align, width, hideOnMobile }] (required)
 *   rows            - array of row data (required)
 *   renderRow       - (row, index) => <TableRow>...</TableRow> — the caller
 *                      supplies the actual row markup/cells, since existing
 *                      pages have very different per-row structures
 *                      (expandable detail rows, action menus, etc.)
 *   getRowKey       - (row, index) => string|number, for the React `key`
 *                      (default: index)
 *   loading         - boolean; shows LoadingState instead of rows/empty
 *   loadingVariant  - passed through to LoadingState ("spinner" | "rows")
 *   emptyTitle      - EmptyState title shown when rows.length === 0
 *   emptyDescription- EmptyState description
 *   emptyAction     - { label, onAction } for EmptyState's optional button
 *   stickyHeader    - boolean (default true)
 *   maxHeight       - optional max height for the scroll container
 */
export default function DataTable({
  columns,
  rows,
  renderRow,
  getRowKey,
  loading = false,
  loadingVariant = "rows",
  emptyTitle = "No records found",
  emptyDescription,
  emptyAction,
  stickyHeader = true,
  maxHeight,
}) {
  const isEmpty = !loading && (!rows || rows.length === 0);

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ maxHeight, borderRadius: 2 }}
    >
      <Table stickyHeader={stickyHeader} size="small">
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell
                key={col.key}
                align={col.align || "left"}
                sx={{
                  width: col.width,
                  fontWeight: 700,
                  fontSize: 12.5,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  display: col.hideOnMobile
                    ? { xs: "none", sm: "table-cell" }
                    : undefined,
                }}
              >
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && (
            <TableRow>
              <TableCell colSpan={columns.length} sx={{ border: 0, p: 0 }}>
                <LoadingState variant={loadingVariant} columns={columns.length} />
              </TableCell>
            </TableRow>
          )}

          {isEmpty && (
            <TableRow>
              <TableCell colSpan={columns.length} sx={{ border: 0 }}>
                <EmptyState
                  title={emptyTitle}
                  description={emptyDescription}
                  actionLabel={emptyAction?.label}
                  onAction={emptyAction?.onAction}
                />
              </TableCell>
            </TableRow>
          )}

          {!loading &&
            !isEmpty &&
            rows.map((row, i) => (
              <Fragment key={getRowKey ? getRowKey(row, i) : i}>
                {renderRow(row, i)}
              </Fragment>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
